import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getDefaultFeatureFlags } from '@/config/features';
import { logger } from '@/utils';
import { ContextBuilder } from './ContextBuilder';

export type FeatureFlags = {
  [key: string]: boolean;
};

type FeaturesContextValue = {
  features: FeatureFlags;
  isFeatureEnabled: (featureName: string) => boolean;
  enableFeature: (featureName: string) => void;
  disableFeature: (featureName: string) => void;
  setFeatures: (features: FeatureFlags) => void;
};

const FeaturesContext = new ContextBuilder<FeaturesContextValue>(
  'FeaturesContext',
);

export const useFeatures = FeaturesContext.use;

export const FeaturesProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [features, setFeatures] = useState<FeatureFlags>(() => {
    const defaultFlags = getDefaultFeatureFlags();

    try {
      const stored = localStorage.getItem('features');

      if (stored) {
        const parsedFlags = JSON.parse(stored);
        // Merge stored flags with defaults, giving precedence to stored values
        return { ...defaultFlags, ...parsedFlags };
      }
    } catch {
      logger.error('Failed to parse features from localStorage');
    }
    // Return defaults if nothing is stored or parsing fails
    return defaultFlags;
  });

  useEffect(() => {
    try {
      localStorage.setItem('features', JSON.stringify(features));
    } catch {
      logger.error('Failed to save features to localStorage');
    }
  }, [features]);

  const isFeatureEnabled = useCallback(
    (featureName: string): boolean => features[featureName] === true,
    [features],
  );

  const enableFeature = useCallback(
    (featureName: string) => {
      setFeatures((current) => ({
        ...current,
        [featureName]: true,
      }));
    },
    [setFeatures],
  );

  const disableFeature = useCallback(
    (featureName: string) => {
      setFeatures((current) => ({
        ...current,
        [featureName]: false,
      }));
    },
    [setFeatures],
  );

  const ctx = useMemo<FeaturesContextValue>(() => {
    return {
      features,
      isFeatureEnabled,
      enableFeature,
      disableFeature,
      setFeatures,
    };
  }, [features, isFeatureEnabled, enableFeature, disableFeature]);

  return (
    <FeaturesContext.Context.Provider value={ctx}>
      {children}
    </FeaturesContext.Context.Provider>
  );
};
